import React, { useMemo, useCallback, useState, useEffect } from "react";
import { message, Select, Button, Modal, Input } from "antd";
import { useMsal } from "@azure/msal-react";
import {
  canGenerateLeadAdvisorApprovalMatrix,
  mergeApprovalMatrixEntries,
  buildApprovalMatrixDisplayRows,
  listSubmittedDocxBrandedAssignedRowIds,
  getOrgSetupDelegateArtefactStateFromWiz,
  APPROVAL_MATRIX_SLA_OPTIONS,
  isApprovalMatrixAssignmentsComplete,
} from "../utils/approvalMatrixModel";
import ImpersonationDialog from "./ImpersonationDialog";
import { getActiveImpersonation } from "../utils/impersonationFetch";
import { apiConfig, apiTokenRequest } from "../authConfig";
import { formatDdMmYyyy } from "../utils/formatDdMmYyyy";
import { displayEmail } from '../utils/displayEmail';

/**
 * Feature flag — ON. No pre-existing feature-flag convention exists in this codebase
 * (grepped for FEATURE_FLAG / REACT_APP_FEATURE_* / etc. — nothing; the REACT_APP_* env
 * vars here are all API config, not flags), so this is a plain exported constant per the
 * simplest-viable-mechanism fallback.
 *
 * Devil's Advocate STOP (commit c021db9 follow-up, 2026-09-16) is now CLOSED — both
 * conditions were verified shut in commit `38f5977` (2026-09-18):
 *   1. The org-chart-approval and artefact-release handlers now honor req.effectiveActor
 *      instead of only the caller's real role.
 *   2. The frontend now reflects an active impersonation session's persona in its own
 *      tab/action gating.
 * `38f5977` also carried Engineering Director APPROVE, Compliance Auditor
 * GRANTED-WITH-CONDITIONS (non-blocking), QA Verifier READY (24 suites/135 tests green),
 * and a Lead-Advisor code-level walkthrough pass. The button, its dialog wiring, and
 * `resolveApprovalMatrixActOnBehalfTarget()` are live for real users as of this flip —
 * do not flip this back to `false` without re-opening the gaps above.
 *
 * Exposed as a sealed object (not a bare `const boolean`) so the test file can force it
 * off for the explicit-opt-out regression tests by mutating
 * `FEATURE_FLAGS.ACT_ON_BEHALF_ENABLED` — a bare exported const, once compiled to
 * CommonJS by Babel, can't be reassigned from a requiring module in a way this file's
 * internal reads would ever observe (one-way exports sync); a shared object's property
 * mutation has no such gap.
 */
export const FEATURE_FLAGS = Object.seal({ ACT_ON_BEHALF_ENABLED: true });

/**
 * R026/R027 (Approval matrix — LA override / "remove dependencies on prior-step approval").
 * Workflow doc slide 9 (Organizational setup: approval matrix) + PRD Stage 5.
 * Per .claude/design/approval-matrix-clarification.md ("Read B"): Commandment V's gates are
 * NOT weakened here — the LA cannot self-approve a blocking persona's step. Instead, each
 * blocking condition below offers a discoverable path into the existing, audited impersonation
 * session (ImpersonationDialog / impersonation.controller.ts) so the LA can legitimately act
 * on that persona's behalf, with a stated reason, while the workflow gate stays intact.
 */

/**
 * Maps this panel's own blocking conditions (identical precedence order to `statusMessage`
 * below) to the persona who owns the pending action — so the "Act on behalf of…" affordance
 * always names the actual blocker, never a generic LA self-approval.
 *
 * `actionKey` is a stable dispatch token for the two SPOC-owned actions this precedence chain
 * can resolve to — it lets a caller (the completion-button wiring below) pick the right
 * impersonated callback prop without re-deriving or string-matching `actionLabel`, which is
 * display prose and free to reword. `null` for the two delegate-owned outcomes: there is no
 * impersonated completion action for those yet (delegate docx submission is out of scope this
 * lap — see the completion-button render logic below).
 * @param {boolean} orgChartApproved
 * @param {object} wiz
 * @param {unknown[]} artefactIndexRows
 * @returns {{ personaToken: 'company_spoc' | 'company_delegate', personaLabel: string, actionLabel: string, actionKey: 'org_chart_approve' | 'artefacts_approve_release' | null } | null}
 */
function resolveApprovalMatrixActOnBehalfTarget(orgChartApproved, wiz, artefactIndexRows) {
  if (!orgChartApproved) {
    return {
      personaToken: "company_spoc",
      personaLabel: "Company SPOC",
      actionLabel: "Approve the organizational chart",
      actionKey: "org_chart_approve",
    };
  }
  const delegateState = getOrgSetupDelegateArtefactStateFromWiz(wiz, artefactIndexRows);
  if (!delegateState.released) {
    return {
      personaToken: "company_spoc",
      personaLabel: "Company SPOC",
      actionLabel: "Approve & release the branded company artefact documents to delegates",
      actionKey: "artefacts_approve_release",
    };
  }
  if (delegateState.pendingDelegateDocx) {
    return {
      personaToken: "company_delegate",
      personaLabel: "Company Delegate",
      actionLabel: "Submit the assigned branded company artefact document(s)",
      actionKey: null,
    };
  }
  const rowIds = listSubmittedDocxBrandedAssignedRowIds(wiz, artefactIndexRows);
  if (rowIds.length === 0) {
    return {
      personaToken: "company_delegate",
      personaLabel: "Company Delegate",
      actionLabel: "Submit the assigned branded company artefact document(s)",
      actionKey: null,
    };
  }
  return null;
}

const ROLE_BADGE_CLASS = {
  LEAD_ADVISOR: "border-[color:var(--color-accent-fill)] text-[color:var(--color-accent)] bg-[color:var(--color-info-bg)]",
  COMPANY_SPOC: "border-[color:var(--color-accent-fill)] text-[color:var(--color-ok-fg)] bg-[color:var(--color-ok-bg)]",
  COMPANY_DELEGATE: "border-[color:var(--color-border-strong)] text-[color:var(--color-text-hero)] bg-[color:var(--color-bg-alt)]",
  ADVISOR: "border-[color:var(--color-accent-fill)] text-[color:var(--color-accent)] bg-[color:var(--color-info-bg)]",
  UNKNOWN: "border-[color:var(--color-border-strong)] text-[color:var(--color-text-muted)] bg-[color:var(--color-surface-sunken)]",
};

function roleLabel(token) {
  return String(token || "UNKNOWN").replace(/_/g, " ");
}

function memberSelectLabel(m) {
  const role = String(m.role || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return `${m.name || "Member"} · ${role}`;
}

function memberNameById(members, id) {
  const m = (Array.isArray(members) ? members : []).find((mm) => String(mm.id) === String(id));
  return m?.name || displayEmail(m?.email) || "the selected project member";
}

/**
 * "Send for review" — UAT backlog row 24. Mirrors the backend's cap
 * (`MAX_APPROVAL_MATRIX_REVIEW_NOTE_LENGTH`, `approvalMatrixReviewRequest.ts`) — this is a
 * UI-side counter only, the backend remains the source of truth and re-validates on submit.
 */
const MAX_REVIEW_NOTE_LENGTH = 2000;

/**
 * @param {object} props
 * @param {object} props.wiz
 * @param {unknown[]} props.artefactIndexRows
 * @param {unknown[]} props.normalizedOrgRows
 * @param {unknown[]} props.projectMembers
 * @param {(patch: object) => Promise<boolean>} props.persistWizard
 * @param {boolean} props.busy
 * @param {(busy: boolean) => void} [props.setBusy]
 * @param {boolean} props.orgChartApproved
 * @param {boolean} [props.readOnly]
 * @param {() => void} [props.onAfterGenerate]
 * @param {() => void} [props.onContinueToRiskKpis] — when matrix rows all have escalation + SLA
 * @param {string} [props.projectId] — required to render the "Act on behalf of…" affordance;
 *   omit only in a context where impersonation isn't offered (e.g. a read-only preview).
 * @param {string} [props.projectName] — display label for the impersonation session banner.
 * @param {() => Promise<boolean>} [props.onActOnBehalfOrgChartApprove] — completes "Approve the
 *   organizational chart" as an already-active, matching `company_spoc` impersonation session
 *   (`OrganizationalSetup.jsx::onSpocApproveOrgChartImpersonated`). Only ever invoked when
 *   FEATURE_FLAGS.ACT_ON_BEHALF_ENABLED is true and a matching session exists — see the
 *   completion-button render logic below.
 * @param {() => Promise<boolean>} [props.onActOnBehalfArtefactsApproveRelease] — sibling of the
 *   above for "Approve & release the branded company artefact documents to delegates"
 *   (`OrganizationalSetup.jsx::onSpocApproveAndReleaseCompanyArtefactsDocImpersonated`).
 */
export function LeadAdvisorApprovalMatrixPanel({
  wiz,
  artefactIndexRows,
  normalizedOrgRows,
  projectMembers,
  persistWizard,
  busy,
  setBusy,
  orgChartApproved,
  readOnly = false,
  onAfterGenerate,
  onContinueToRiskKpis,
  projectId,
  projectName,
  onActOnBehalfOrgChartApprove,
  onActOnBehalfArtefactsApproveRelease,
}) {
  const [localBusy, setLocalBusy] = useState(false);
  const saving = busy || localBusy;
  const runBusy = setBusy ?? setLocalBusy;
  const [actOnBehalfOpen, setActOnBehalfOpen] = useState(false);

  // Live impersonation session, polled — sessionStorage writes don't trigger a React re-render
  // on their own (same reasoning as ImpersonationBanner.jsx's own 15s poll), and this panel
  // needs to know the instant a session started via its own dialog (or elsewhere in the tab)
  // becomes a match, so it can swap the "Act on behalf of…" starter button for the completion
  // button below without requiring a manual refresh.
  const [activeImpersonationSession, setActiveImpersonationSession] = useState(() => getActiveImpersonation());
  useEffect(() => {
    const tick = () => setActiveImpersonationSession(getActiveImpersonation());
    tick();
    const timer = setInterval(tick, 15000);
    return () => clearInterval(timer);
  }, []);

  /**
   * "Send for review" (UAT backlog row 24). User ruling (verbatim): "Keep it flexible, it
   * should have a capability to send for review to a project team member, primarily a SPOC
   * but no need to hard guard it." This is a NOTIFICATION/ROUTING action — it does not
   * approve the matrix or any workflow step (Commandment V: never imply a gate was skipped),
   * and it is a wholly different mechanism from "Act on behalf of…" above (which completes an
   * impersonated persona's own blocking action). Trigger role (Lead Advisor / platform_admin)
   * is enforced server-side; this panel is already LA-only, so no extra client gate is added.
   *
   * POST /projects/:id/organizational-setup/approval-matrix/send-for-review
   * (organizationalSetupApprovalMatrix.controller.ts). Recipient is ANY ProjectMember —
   * deliberately not restricted to SPOC; the SPOC-first sort in `reviewRecipientOptions`
   * below is a UI convenience only, per the ruling's "no need to hard guard it".
   */
  const { instance } = useMsal();
  const [sendForReviewOpen, setSendForReviewOpen] = useState(false);
  const [reviewRecipientId, setReviewRecipientId] = useState(undefined);
  const [reviewNote, setReviewNote] = useState("");
  const [sendingReview, setSendingReview] = useState(false);
  const [sendReviewError, setSendReviewError] = useState(null);

  // Seeded from the wizard, then appended to optimistically on a successful send. This
  // endpoint writes `leadAdvisorApprovalMatrixReviewRequests` directly (NOT through
  // `persistWizard`'s generic profile PUT — that path is blocked server-side, see
  // `assertApprovalMatrixReviewRequestsSaveAllowed`), so the parent's `wiz` prop won't reflect
  // a fresh send until the project reloads; the local copy lets "Sent to X on Y" update
  // immediately without waiting on a full refetch.
  const [localReviewRequests, setLocalReviewRequests] = useState(() => {
    const arr = wiz?.leadAdvisorApprovalMatrixReviewRequests;
    return Array.isArray(arr) ? arr : [];
  });
  useEffect(() => {
    const arr = wiz?.leadAdvisorApprovalMatrixReviewRequests;
    setLocalReviewRequests(Array.isArray(arr) ? arr : []);
  }, [wiz?.leadAdvisorApprovalMatrixReviewRequests]);

  const mostRecentReviewRequest = useMemo(() => {
    if (!localReviewRequests.length) return null;
    return localReviewRequests.reduce(
      (best, r) => (!best || String(r?.sentAt) > String(best?.sentAt) ? r : best),
      null,
    );
  }, [localReviewRequests]);

  const reviewRecipientOptions = useMemo(() => {
    const members = Array.isArray(projectMembers) ? projectMembers : [];
    // SPOC-first sort — UI convenience matching the ruling's "primarily a SPOC"; every member
    // stays selectable (Array.prototype.sort is stable, so relative order is otherwise kept).
    return [...members]
      .sort((a, b) => (a.role === "company_spoc" ? 0 : 1) - (b.role === "company_spoc" ? 0 : 1))
      .map((m) => ({ value: String(m.id), label: memberSelectLabel(m) }));
  }, [projectMembers]);

  const openSendForReview = useCallback(() => {
    setSendReviewError(null);
    setReviewNote("");
    setReviewRecipientId(reviewRecipientOptions[0]?.value);
    setSendForReviewOpen(true);
  }, [reviewRecipientOptions]);

  const onSubmitSendForReview = useCallback(async () => {
    if (!reviewRecipientId || sendingReview || !projectId) return;
    setSendingReview(true);
    setSendReviewError(null);
    try {
      const account = instance.getActiveAccount();
      const tokenResponse = await instance.acquireTokenSilent({ ...apiTokenRequest, account });
      const res = await fetch(
        `${apiConfig.baseUrl}/api/v1/projects/${projectId}/organizational-setup/approval-matrix/send-for-review`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokenResponse.accessToken}`,
          },
          body: JSON.stringify({
            recipientMemberId: reviewRecipientId,
            ...(reviewNote.trim() ? { note: reviewNote.trim() } : {}),
          }),
        },
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        // Surfaces the documented error shapes as-is (400 incomplete matrix / 400 invalid
        // recipient / 403 non-LA) — none should normally fire given this panel's own gating,
        // but the backend is the source of truth and its message is shown verbatim.
        throw new Error(json?.error?.message || `Could not send for review (HTTP ${res.status}).`);
      }
      const entry = json.data?.reviewRequest;
      if (entry) setLocalReviewRequests((prev) => [...prev, entry]);
      message.success(`Sent for review to ${memberNameById(projectMembers, reviewRecipientId)}.`);
      setSendForReviewOpen(false);
    } catch (err) {
      setSendReviewError(err?.message || "Could not send for review.");
    } finally {
      setSendingReview(false);
    }
  }, [reviewRecipientId, reviewNote, sendingReview, projectId, instance, projectMembers]);

  const matrixMap = useMemo(() => {
    const m = wiz?.leadAdvisorApprovalMatrixByRowId;
    return m && typeof m === "object" && !Array.isArray(m) ? { ...m } : {};
  }, [wiz?.leadAdvisorApprovalMatrixByRowId]);

  const generateCheck = useMemo(
    () => canGenerateLeadAdvisorApprovalMatrix(wiz, artefactIndexRows, orgChartApproved),
    [wiz, artefactIndexRows, orgChartApproved],
  );

  const matrixRowIds = useMemo(() => {
    const keys = Object.keys(matrixMap).filter((k) => matrixMap[k] && typeof matrixMap[k] === "object");
    if (keys.length > 0) return keys.sort();
    return listSubmittedDocxBrandedAssignedRowIds(wiz, artefactIndexRows).sort();
  }, [matrixMap, wiz, artefactIndexRows]);

  const displayRows = useMemo(
    () => buildApprovalMatrixDisplayRows(matrixRowIds, artefactIndexRows, normalizedOrgRows, projectMembers),
    [matrixRowIds, artefactIndexRows, normalizedOrgRows, projectMembers],
  );

  const allRowAssignmentsComplete = useMemo(
    () => isApprovalMatrixAssignmentsComplete(matrixMap, matrixRowIds),
    [matrixMap, matrixRowIds],
  );

  const memberOptions = useMemo(
    () =>
      (Array.isArray(projectMembers) ? projectMembers : []).map((m) => ({
        value: String(m.id),
        label: memberSelectLabel(m),
      })),
    [projectMembers],
  );

  const statusMessage = useMemo(() => {
    if (!orgChartApproved) return "Waiting for company SPOC approval of the organizational chart.";
    const delegateState = getOrgSetupDelegateArtefactStateFromWiz(wiz, artefactIndexRows);
    if (!delegateState.released) return "Branded documents have not been released to delegates yet.";
    if (delegateState.pendingDelegateDocx) {
      return "Waiting for company delegates to submit all assigned Word (.docx) documents.";
    }
    const rowIds = listSubmittedDocxBrandedAssignedRowIds(wiz, artefactIndexRows);
    if (rowIds.length === 0) return "No submitted assigned branded .docx documents are on record yet.";
    return null;
  }, [orgChartApproved, wiz, artefactIndexRows]);

  // Same precedence chain as `statusMessage` above, resolved to WHO owns the pending action —
  // not rendered at all once readOnly (the matrix is already finalized by then), and still
  // gated behind FEATURE_FLAGS.ACT_ON_BEHALF_ENABLED (see the flag's doc comment above; now
  // `true` by default as of 38f5977) so it can be forced off for a specific session or test.
  const actOnBehalfTarget = useMemo(() => {
    if (!FEATURE_FLAGS.ACT_ON_BEHALF_ENABLED || readOnly || !statusMessage) return null;
    return resolveApprovalMatrixActOnBehalfTarget(orgChartApproved, wiz, artefactIndexRows);
  }, [readOnly, statusMessage, orgChartApproved, wiz, artefactIndexRows]);

  const actOnBehalfReasonPrefill = actOnBehalfTarget
    ? `Blocked on: ${actOnBehalfTarget.actionLabel} (approval matrix stage).`
    : "";

  // True only when a LIVE (non-expired — getActiveImpersonation() already checks expiresAt),
  // role-matching, project-matching impersonation session exists for the persona currently
  // blocking this stage. Starting a second session for the same blocker would be pointless, so
  // the starter "Act on behalf of…" button below is suppressed once this is true, in favour of
  // the completion button.
  const matchingImpersonationActive = useMemo(() => {
    if (!actOnBehalfTarget || !activeImpersonationSession) return false;
    return (
      activeImpersonationSession.effectiveRole === actOnBehalfTarget.personaToken &&
      activeImpersonationSession.projectId === projectId
    );
  }, [actOnBehalfTarget, activeImpersonationSession, projectId]);

  // Company-delegate-blocked cases (`actionKey: null`) deliberately have no completion handler
  // here — the backend gate for delegate docx submission is NOT in this lap's scope (only the
  // two SPOC-owned fields, org-chart approval and artefact approve+release, are wired). This is
  // a known, deliberate residual, not an oversight.
  const impersonatedActionHandler =
    actOnBehalfTarget?.actionKey === "org_chart_approve"
      ? onActOnBehalfOrgChartApprove
      : actOnBehalfTarget?.actionKey === "artefacts_approve_release"
        ? onActOnBehalfArtefactsApproveRelease
        : null;

  const [completing, setCompleting] = useState(false);
  const onCompleteActOnBehalf = useCallback(async () => {
    if (!impersonatedActionHandler || completing) return;
    setCompleting(true);
    try {
      await impersonatedActionHandler();
    } finally {
      setCompleting(false);
    }
  }, [impersonatedActionHandler, completing]);

  const onGenerate = useCallback(async () => {
    if (readOnly) return;
    const check = canGenerateLeadAdvisorApprovalMatrix(wiz, artefactIndexRows, orgChartApproved);
    if (!check.ok || !check.rowIds) {
      message.warning("Cannot generate yet - complete delegate submissions first.");
      return;
    }
    runBusy(true);
    try {
      const next = mergeApprovalMatrixEntries(wiz.leadAdvisorApprovalMatrixByRowId, check.rowIds);
      const patch = {
        leadAdvisorApprovalMatrixByRowId: next,
        leadAdvisorApprovalMatrixGeneratedAt:
          wiz.leadAdvisorApprovalMatrixGeneratedAt || new Date().toISOString(),
      };
      const ok = await persistWizard(patch);
      if (ok) {
        message.success("Approval matrix updated from submitted artefacts.");
        onAfterGenerate?.();
      }
    } finally {
      runBusy(false);
    }
  }, [
    wiz,
    artefactIndexRows,
    orgChartApproved,
    persistWizard,
    runBusy,
    onAfterGenerate,
    readOnly,
  ]);

  const patchMatrixCell = useCallback(
    async (templateRowId, field, value) => {
      if (readOnly) return;
      const id = String(templateRowId);
      const allIds = listSubmittedDocxBrandedAssignedRowIds(wiz, artefactIndexRows);
      const base = mergeApprovalMatrixEntries(matrixMap, allIds);
      const prevEntry = base[id] && typeof base[id] === "object" ? { ...base[id] } : {};
      const nextEntry = {
        escalationMemberId:
          field === "escalationMemberId"
            ? value && String(value).trim()
              ? String(value)
              : null
            : prevEntry.escalationMemberId != null && String(prevEntry.escalationMemberId).trim()
              ? String(prevEntry.escalationMemberId)
              : null,
        slaKey:
          field === "slaKey"
            ? value && String(value).trim()
              ? String(value)
              : null
            : prevEntry.slaKey != null && String(prevEntry.slaKey).trim()
              ? String(prevEntry.slaKey)
              : null,
      };
      const nextMap = { ...base, [id]: nextEntry };
      runBusy(true);
      try {
        const ok = await persistWizard({ leadAdvisorApprovalMatrixByRowId: nextMap });
        if (!ok) message.error("Could not save.");
      } finally {
        runBusy(false);
      }
    },
    [matrixMap, persistWizard, runBusy, wiz, artefactIndexRows, readOnly],
  );

  const onEscalationChange = (templateRowId, v) => {
    void patchMatrixCell(templateRowId, "escalationMemberId", v);
  };

  const onSlaChange = (templateRowId, v) => {
    void patchMatrixCell(templateRowId, "slaKey", v);
  };

  // Enabled only once the matrix is actually complete — mirrors the backend's own C2
  // completeness check (`isApprovalMatrixAssignmentsCompleteMinimal`,
  // approvalMatrixReviewRequest.ts) so the UI prevents the 400 proactively rather than just
  // handling it reactively.
  const sendForReviewDisabled =
    readOnly || saving || sendingReview || displayRows.length === 0 || !allRowAssignmentsComplete;

  const matrixLockedAfterContinue =
    typeof wiz?.leadAdvisorApprovalMatrixContinuedAt === "string" &&
    wiz.leadAdvisorApprovalMatrixContinuedAt.trim().length > 0;

  return (
    <>
    <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-8 shadow-xl">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-sm font-black uppercase tracking-tight text-[color:var(--color-text-hero)]">Approval matrix</h3>
          <p className="mt-2 max-w-2xl text-sm font-medium text-[color:var(--color-text-muted)]">
            Who approves what - with designation, project role, escalation, and SLA. Approver and designation come from
            each personnel row&apos;s <span className="font-semibold text-[color:var(--color-text-hero)]">Reports to</span> column in table
            order, repeating across documents when there are more matrix rows than personnel rows. Project role matches
            the approver name to project members. Generate rows after all assigned branded Word documents are submitted.
          </p>
        </div>
        <div className="flex flex-shrink-0 flex-col gap-2 sm:items-end">
          <Button
            type="primary"
            disabled={readOnly || saving || !generateCheck.ok}
            loading={saving}
            onClick={() => void onGenerate()}
            className="!h-auto !rounded-xl !px-5 !py-3 !text-[10px] !font-black !uppercase !tracking-widest"
          >
            Generate / refresh matrix
          </Button>
          <Button
            type="default"
            disabled={sendForReviewDisabled}
            title={
              displayRows.length === 0 || !allRowAssignmentsComplete
                ? "Every row needs an escalation contact and SLA assigned before the approval matrix can be sent for review."
                : "Notify a project team member to review the approval matrix - this does not approve it."
            }
            onClick={openSendForReview}
            className="!h-auto !rounded-xl !border-[color:var(--color-border-strong)] !bg-[color:var(--color-bg-white)] !px-5 !py-3 !text-[10px] !font-black !uppercase !tracking-widest"
          >
            Send for review
          </Button>
        </div>
      </div>

      {readOnly && matrixLockedAfterContinue ? (
        <div className="mt-4 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-4 py-3 text-sm font-medium text-[color:var(--color-text-muted-dark)]">
          You continued to risk assessment &amp; KPIs - this matrix is read-only. Escalation and SLA stay as saved.
        </div>
      ) : null}

      {mostRecentReviewRequest ? (
        <div className="mt-4 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-4 py-3 text-sm font-medium text-[color:var(--color-text-muted-dark)]">
          Last sent for review to{" "}
          <span className="font-semibold text-[color:var(--color-text-hero)]">
            {memberNameById(projectMembers, mostRecentReviewRequest.recipientMemberId)}
          </span>{" "}
          on {formatDdMmYyyy(mostRecentReviewRequest.sentAt) || "-"}.
          {mostRecentReviewRequest.note ? (
            <span className="mt-1 block text-[color:var(--color-text-muted)]">Note: {mostRecentReviewRequest.note}</span>
          ) : null}
        </div>
      ) : null}

      {statusMessage ? (
        <div className="mt-4 rounded-xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] px-4 py-3 text-sm font-semibold text-[color:var(--color-warn-fg)]">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <span>{statusMessage}</span>
            {/*
              company_spoc-blocked: swap the starter button for the completion button once a
              matching session is live (starting a second session for the same blocker is
              pointless). company_delegate-blocked (`actionKey: null`): there is no completion
              surface for this yet — OrganizationalSetup.jsx's delegate-facing tab is gated on
              the real `company_delegate` role only, so an LA impersonating that persona can
              never reach a place to finish the action. Devil's Advocate PROCEED-WITH-CONDITIONS
              (2026-09-18): starting a real, audited, time-boxed session that can never be
              completed is a dead end dressed as a live affordance — render a disabled button
              with an explanatory title instead of a live starter, mirroring this file's existing
              disabled-with-title precedent. Revisit
              once delegate docx submission gets an impersonated completion path.
            */}
            {actOnBehalfTarget && projectId && actOnBehalfTarget.personaToken === "company_spoc" ? (
              matchingImpersonationActive && impersonatedActionHandler ? (
                <Button
                  type="link"
                  loading={completing}
                  onClick={() => void onCompleteActOnBehalf()}
                  aria-label={`Complete: ${actOnBehalfTarget.actionLabel}, acting on behalf of ${actOnBehalfTarget.personaLabel}`}
                  className="!h-auto !flex-shrink-0 !p-0 !text-[11px] !font-black !uppercase !tracking-widest"
                >
                  Complete: {actOnBehalfTarget.actionLabel} (as {actOnBehalfTarget.personaLabel}) &rarr;
                </Button>
              ) : (
                <Button
                  type="link"
                  onClick={() => setActOnBehalfOpen(true)}
                  aria-label={`Act on behalf of ${actOnBehalfTarget.personaLabel} to ${actOnBehalfTarget.actionLabel}`}
                  className="!h-auto !flex-shrink-0 !p-0 !text-[11px] !font-black !uppercase !tracking-widest"
                >
                  Act on behalf of {actOnBehalfTarget.personaLabel} &rarr;
                </Button>
              )
            ) : actOnBehalfTarget && projectId ? (
              <Button
                type="link"
                disabled
                title="Acting on behalf of Company Delegate isn't available yet - there is no way to complete this action after starting the session."
                aria-label={`Acting on behalf of ${actOnBehalfTarget.personaLabel} is not available yet`}
                className="!h-auto !flex-shrink-0 !cursor-not-allowed !p-0 !text-[11px] !font-black !uppercase !tracking-widest"
              >
                Act on behalf of {actOnBehalfTarget.personaLabel} &rarr;
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      {displayRows.length === 0 ? (
        <p className="mt-8 text-sm font-medium text-[color:var(--color-text-muted)]">
          No submitted branded .docx assignments yet. When delegates have submitted their documents, use{" "}
          <span className="font-semibold text-[color:var(--color-text-hero)]">Generate / refresh matrix</span> to create rows.
        </p>
      ) : (
        <div className="mt-8 overflow-x-auto rounded-2xl border border-[color:var(--color-border)] shadow-sm">
          <table className="w-full min-w-[960px] text-left text-sm">
            <thead>
              <tr className="bg-[color:var(--color-surface-sunken)] text-[10px] font-black uppercase tracking-[0.15em] text-[color:var(--color-accent)]">
                <th className="px-4 py-4">#</th>
                <th className="px-4 py-4 min-w-[200px]">Branded artefact</th>
                <th className="px-4 py-4 min-w-[120px]">Approver</th>
                <th className="px-4 py-4 min-w-[120px]">Designation</th>
                <th className="px-4 py-4 min-w-[100px]">Role</th>
                <th className="px-4 py-4 min-w-[200px]">Escalation</th>
                <th className="px-4 py-4 min-w-[160px]">SLA</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--color-border)]">
              {displayRows.map((row, i) => {
                const entry = matrixMap[row.templateRowId] || {};
                const escVal =
                  entry.escalationMemberId != null && String(entry.escalationMemberId).trim()
                    ? String(entry.escalationMemberId)
                    : undefined;
                const slaVal =
                  entry.slaKey != null && String(entry.slaKey).trim() ? String(entry.slaKey) : undefined;
                const token = row.approverRoleToken || "UNKNOWN";
                return (
                  <tr key={row.templateRowId} className="font-medium text-[color:var(--color-text-hero)]">
                    <td className="px-4 py-3 text-[color:var(--color-text-muted)]">{i + 1}</td>
                    <td className="px-4 py-3">
                      <span className="line-clamp-2 text-xs font-semibold text-[color:var(--color-text-hero)]">{row.title || "-"}</span>
                      <span className="mt-0.5 block break-all text-[11px] text-[color:var(--color-text-muted)]">{row.fileLabel || "-"}</span>
                    </td>
                    <td className="px-4 py-3 text-sm">{row.approverName || "-"}</td>
                    <td className="px-4 py-3 text-sm">{row.approverDesignation || "-"}</td>
                    <td className="px-4 py-3">
                      {row.approverRoleToken ? (
                        <span
                          className={`inline-block rounded-lg border px-2 py-0.5 text-[9px] font-black uppercase tracking-widest ${ROLE_BADGE_CLASS[token] || ROLE_BADGE_CLASS.UNKNOWN}`}
                        >
                          {roleLabel(token)}
                        </span>
                      ) : (
                        <span className="text-[color:var(--color-text-muted)]">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Select
                        className="w-full min-w-[180px]"
                        placeholder="Select member"
                        allowClear
                        disabled={readOnly || saving || memberOptions.length === 0}
                        options={memberOptions}
                        value={escVal}
                        onChange={(v) => onEscalationChange(row.templateRowId, v)}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <Select
                        className="w-full min-w-[160px]"
                        placeholder="SLA"
                        allowClear
                        disabled={readOnly || saving}
                        options={APPROVAL_MATRIX_SLA_OPTIONS}
                        value={slaVal}
                        onChange={(v) => onSlaChange(row.templateRowId, v)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!readOnly && !statusMessage && displayRows.length > 0 && allRowAssignmentsComplete && onContinueToRiskKpis ? (
        <div className="mt-8 flex flex-col items-stretch gap-3 border-t border-[color:var(--color-border)] pt-6 sm:flex-row sm:items-center sm:justify-end">
          <p className="text-sm font-medium text-[color:var(--color-text-muted)] sm:mr-auto sm:max-w-md">
            Escalation and SLA are set for every row. You can continue to risk assessment scale and KPIs.
          </p>
          <Button
            type="primary"
            disabled={readOnly || saving}
            onClick={() => onContinueToRiskKpis()}
            className="!h-auto !rounded-xl !px-5 !py-3 !text-[10px] !font-black !uppercase !tracking-widest"
          >
            Continue to risk assessment & KPIs
          </Button>
        </div>
      ) : null}
    </div>
    {actOnBehalfTarget && projectId ? (
      <ImpersonationDialog
        open={actOnBehalfOpen}
        onClose={() => setActOnBehalfOpen(false)}
        projectId={projectId}
        projectName={projectName}
        members={projectMembers}
        initialEffectiveRole={actOnBehalfTarget.personaToken}
        initialReason={actOnBehalfReasonPrefill}
      />
    ) : null}
    <Modal
      open={sendForReviewOpen}
      onCancel={() => (sendingReview ? null : setSendForReviewOpen(false))}
      title="Send approval matrix for review"
      okText="Send for review"
      okButtonProps={{ disabled: !reviewRecipientId, loading: sendingReview }}
      cancelButtonProps={{ disabled: sendingReview }}
      onOk={() => void onSubmitSendForReview()}
      destroyOnClose
      width={480}
    >
      <p className="mb-4 text-sm text-[color:var(--color-text-muted)]">
        This notifies the selected project team member so they can review the approval matrix.
        It does not approve the matrix or any workflow step.
      </p>
      <div className="mb-4">
        <label className="mb-1 block text-xs font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
          Recipient
        </label>
        <Select
          className="w-full"
          placeholder="Select a project member"
          options={reviewRecipientOptions}
          value={reviewRecipientId}
          onChange={setReviewRecipientId}
          showSearch
          optionFilterProp="label"
          disabled={sendingReview}
        />
      </div>
      <div>
        <label className="mb-1 block text-xs font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
          Note (optional)
        </label>
        <Input.TextArea
          rows={4}
          maxLength={MAX_REVIEW_NOTE_LENGTH}
          showCount
          value={reviewNote}
          onChange={(e) => setReviewNote(e.target.value)}
          placeholder="Add context for the reviewer (optional)."
          disabled={sendingReview}
        />
      </div>
      {sendReviewError ? (
        <div className="mt-3 rounded-lg border border-[color:var(--color-danger-line)] bg-[color:var(--color-danger-bg)] px-3 py-2 text-xs font-semibold text-[color:var(--color-danger-fg)]">
          {sendReviewError}
        </div>
      ) : null}
    </Modal>
    </>
  );
}
